import React, { useEffect, useState } from "react";
import moment from "moment";

import MiniSpinner from "@Components/shared/MiniSpinner";
import { IUserActivityMinimal } from "@Models/IActivityDetails";

import "@Styles/components/activityCalendar.scss";

interface IDate {
    date: Date;
    active: boolean;
}

interface IWeekDays {
    date: Date;
    weekDay: number;
    monthDay: number;
    month: string;
    active: boolean;
}

interface IProps {
    userMinimal: IUserActivityMinimal[];
    isFetching: boolean;
}

const ActivityCalendar: React.FC<IProps> = ({ userMinimal, isFetching }: IProps) => {
    const [monthRange, setMonthRange] = useState<IWeekDays[][]>([]);
    const [totalActiveDays, setTotalActiveDays] = useState<number>(0);
    const [weeksStreak, setWeeksStreak] = useState<number>(0);
    const [rangeDates, setRangeDates] = useState<{ latestDate: Date | string; startDate: Date | string }>({
        latestDate: "",
        startDate: "",
    });

    const getWeekDays = (dates: IDate[]) => {
        /** weekDays: ISO Days of Week ( Monday: 1, Sunday: 7 ) **/
        const weekDays: IWeekDays[] = dates.map((day) => ({
            date: day.date,
            weekDay: moment(day.date).isoWeekday(),
            monthDay: Number(moment(day.date).format("D")),
            month: moment(day.date).format("MMM"),
            active: day.active,
        }));
        return weekDays;
    };

    const createWeeks = (weekDays: IWeekDays[]) => {
        const days = [...weekDays];
        let currentWeakNum = 0;
        /* "result" - array with 6 weeks/arrays */
        const result: IWeekDays[][] = [[], [], [], [], [], []];

        days.forEach((day) => {
            if (day.weekDay === 7) {
                result[currentWeakNum].push(day);
                currentWeakNum += 1;
            } else {
                result[currentWeakNum].push(day);
            }
        });
        return result;
    };

    const countWeeksStreak = (weeks: IWeekDays[][]) => {
        let result = 0;
        let isFirstStreakFound = false;
        for (let i = weeks.length - 1; i > 0; i--) {
            if (weeks[i].find((day) => day.active) && weeks[i - 1].find((day) => day.active)) {
                isFirstStreakFound = true;
                result += 1;
            } else if (isFirstStreakFound) {
                break;
            }
        }
        return !result ? 0 : result + 1;
    };

    const countTotalActiveDays = (days: IDate[]) => {
        return days.filter((day) => day.active).length;
    };

    const getMonthRange = (activityDates: IUserActivityMinimal[]) => {
        const latestDate = new Date();
        let startDate = new Date(new Date(latestDate).setDate(latestDate.getDate() - 30));

        /* Check if the "startDate" is not a first day of the week and update it with Monday date from this week */
        if (moment(startDate).isoWeekday() !== 1) {
            const amountMissingWeekDays = moment(startDate).isoWeekday() - 1;
            startDate = new Date(new Date(startDate).setDate(startDate.getDate() - amountMissingWeekDays));
        }
        const sortedDatesArray: IUserActivityMinimal[] = activityDates
            ? activityDates.slice().sort(function (a, b) {
                  const curr = new Date(a.creationDate).getTime();
                  const next = new Date(b.creationDate).getTime();
                  return curr - next;
              })
            : [];

        /* Group all active dates (days) */
        const groupedActivityDates = [];
        let currentGroupedDay = null;
        for (const day of sortedDatesArray) {
            const current = moment(day.creationDate).format("D M YYYY");
            if (!currentGroupedDay) {
                groupedActivityDates.push(day);
                currentGroupedDay = current;
            }
            if (currentGroupedDay !== current) {
                groupedActivityDates.push(day);
                currentGroupedDay = current;
            }
        }

        /* List of date range with "active" status (boolean) */
        const dates: IDate[] = [];
        while (startDate <= latestDate) {
            if (
                groupedActivityDates.find(
                    (date) => moment(date.creationDate).format("D M YYYY") === moment(startDate).format("D M YYYY")
                )
            ) {
                dates.push({ date: new Date(startDate), active: true });
            } else {
                dates.push({ date: new Date(startDate), active: false });
            }
            startDate.setDate(startDate.getDate() + 1);
        }
        return {
            dates,
            latestDate,
            startDate: new Date(new Date(latestDate).setDate(latestDate.getDate() - 30)),
        };
    };

    useEffect(() => {
        const { dates, latestDate, startDate } = getMonthRange(userMinimal);
        const weeks = createWeeks(getWeekDays(dates));
        const amountStreak = countWeeksStreak(weeks);
        const totalActiveDays = countTotalActiveDays(dates);

        setMonthRange(weeks);
        setTotalActiveDays(totalActiveDays);
        setWeeksStreak(amountStreak);
        setRangeDates({ latestDate, startDate });
    }, [userMinimal]);

    return isFetching ? (
        <MiniSpinner />
    ) : (
        <section className="activity-calendar">
            <header className="activity-calendar-header">
                Last 30 Days&nbsp;&nbsp;•&nbsp;&nbsp;
                <span>
                    {moment(rangeDates.startDate).format("D MMM")} - {moment(rangeDates.latestDate).format("D MMM")}
                </span>
            </header>
            <div className="activity-calendar-body">
                <div className="activity-calendar-month">
                    <ul>
                        <li>M</li>
                        <li>T</li>
                        <li>W</li>
                        <li>T</li>
                        <li>F</li>
                        <li>S</li>
                        <li>S</li>
                    </ul>
                    <div className="activity-calendar-dates">
                        {monthRange.map((week) => {
                            return week.map((day, index) => {
                                let activeDayCls = "";
                                let currentRange = "active-range";
                                let currentDay = "";

                                if (new Date(day.date).getTime() < new Date(rangeDates.startDate).getTime()) {
                                    currentRange = "inactive-range";
                                }
                                if (day.active) {
                                    activeDayCls = "active";
                                }
                                if (moment(day.date).format("D M YYYY") === moment().format("D M YYYY")) {
                                    currentDay = "current";
                                }

                                return (
                                    <div
                                        key={index}
                                        className={`day ${activeDayCls} ${currentRange} ${
                                            day.monthDay === 1 ? "first" : ""
                                        }`}
                                    >
                                        {day.monthDay === 1 && <span className="month">{day.month}</span>}
                                        <span className="date">{day.monthDay}</span>
                                        {currentDay && <span className="current" />}
                                    </div>
                                );
                            });
                        })}
                    </div>
                </div>
            </div>
            <footer className="activity-calendar-footer">
                <div>
                    <span className="date-number">{totalActiveDays}</span>
                    <span className="date-text">
                        Active <br /> Days
                    </span>
                </div>
                <div>
                    <span className="date-number">{weeksStreak}</span>
                    <span className="date-text">Week Streak</span>
                </div>
            </footer>
        </section>
    );
};

ActivityCalendar.defaultProps = {
    userMinimal: [],
};

export default ActivityCalendar;
